import { useState } from 'react';
import { ChatService } from '@/api/services/chatService';
import {
  CreateDirectChatParams,
  UnackedMessagesParams,
  GetMessagesParams,
  MessagesListResult,
  ConversationInfo,
  SessionType,
  MessageInfo,
  MessageType
} from '@/api/types';
import { useChatStore } from '@/stores/useChatStore';
import { useFriendStore } from '@/stores/useFriendStore';
import { useGroupStore } from '@/stores/useGroupStore';
import { useTabAuth } from '@/hooks/useTabAuth';
import ToastUtils from '@/utils/toast-utils';
import MessageUtils from '@/utils/ably-im/message-utils';
import { logPrint } from '@/utils/my-utils';
import MessageManager from '@/utils/ably-im/message-manager';
import { BatchProcessorUtils } from '@/utils/batch-processor-utils';
import { useGroupActions } from './useGroupActions';

/**
 * 聊天操作相关的 hook
 * 处理网络请求和数据组装逻辑
 */
export function useChatActions() {
  const [isLoading, setIsLoading] = useState(false);
  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);

  // 获取 store 实例
  const chatStore = useChatStore();

  const { authData } = useTabAuth();

  const groupActions = useGroupActions();

  /**
   * 对话列表排序函数
   * 排序优先级：置顶状态 > 最后消息时间
   * 置顶对话和非置顶对话分别排序，置顶对话永远在前面，内部都按时间排序
   * @param conversations 对话列表
   * @returns 排序后的对话列表
   */
  const sortConversations = (conversations: ConversationInfo[]): ConversationInfo[] => {
    // 分离置顶和非置顶对话
    const pinnedConversations = conversations.filter(conv => conv.is_pinned);
    const unpinnedConversations = conversations.filter(conv => !conv.is_pinned);

    // 置顶对话内部排序：按最后消息时间排序
    const sortedPinned = pinnedConversations.sort((a, b) => {
      const timeA = (a.last_read_at || 0);
      const timeB = (b.last_read_at || 0);
      return timeB - timeA;
    });

    // 非置顶对话排序：按最后消息时间排序
    const sortedUnpinned = unpinnedConversations.sort((a, b) => {
      const timeA = (a.last_read_at || 0);
      const timeB = (b.last_read_at || 0);
      return timeB - timeA;
    });

    // 置顶对话永远在前面，后面跟非置顶对话
    return [...sortedPinned, ...sortedUnpinned];
  };

  /**
   * 组装对话数据，添加标题和头像信息
   * 过滤掉在好友列表或群组列表中找不到的会话（可能已被拉黑或删除）
   * 优化版本：直接使用 store 中维护的 Map，O(1) 查找性能
   * @param conversations 原始对话列表
   * @returns 组装后的对话列表
   */
  const assembleConversationsData = (conversations: ConversationInfo[]): ConversationInfo[] => {
    // 使用 reduce 一次遍历完成过滤和组装
    return conversations.reduce<ConversationInfo[]>((result, conversation) => {
      const assembledConversation: ConversationInfo = { ...conversation };
      let shouldInclude = false;

      if (conversation.session_type === SessionType.Direct) {
        // 私聊：从好友映射表中获取信息（O(1) 查找）
        const friendId = conversation.participants.find(p => p.id !== authData.userId)?.id;
        const friend = friendId ? useFriendStore.getState().friendMap.get(friendId) : undefined;

        if (friend) {
          shouldInclude = true;
          assembledConversation.title = friend.remark || friend.alias || friend.nickname;
          assembledConversation.avatar = friend.avatar;
          assembledConversation.remark = friend.remark;
          assembledConversation.alias = friend.alias;
        }
      } else if (conversation.session_type === SessionType.Group) {
        // 群聊：从群组映射表中获取信息（O(1) 查找）
        const group = useGroupStore.getState().groupMap.get(conversation.id);

        if (group) {
          shouldInclude = true;
          assembledConversation.title = group.name;
          assembledConversation.avatar = group.avatar;
        }
      } else {
        // 其他类型的会话默认保留
        shouldInclude = true;
      }

      // 只有找到对应信息的会话才会被包含在结果中
      if (shouldInclude) {
        result.push(assembledConversation);
      }

      return result;
    }, []);
  };

  /**
   * 组装消息发送者信息
   * @param messages 原始消息列表
   * @returns 组装后的消息列表（按时间正序排序）
   */
  const assembleMessagesData = async (messages: MessageInfo[]): Promise<MessageInfo[]> => {
    const assembledMessages: MessageInfo[] = [];

    for (const message of messages) {
      const assembledMessage: MessageInfo = { ...message };

      // 如果是当前用户发送的消息
      if (message.sender_id === authData.userId) {
        assembledMessage.senderName = '我';
        assembledMessage.senderAvatar = authData.userInfo?.avatar;
        assembledMessages.push(assembledMessage);
        continue;
      }

      // 先从好友列表中查找发送者信息
      const friend = useFriendStore.getState().friendMap.get(message.sender_id);
      if (friend) {
        assembledMessage.senderName = friend.remark || friend.alias || friend.nickname;
        assembledMessage.senderAvatar = friend.avatar;
        assembledMessage.senderRemark = friend.remark;
        assembledMessages.push(assembledMessage);
        continue;
      } else {
        const cachedUser = await useChatStore.getState().getUserByCacheOrNet(message.sender_id);
        if (cachedUser) {
          assembledMessage.senderName = cachedUser.nickname;
          assembledMessage.senderAvatar = cachedUser.avatar;
          assembledMessage.senderRemark = cachedUser.remark;
          assembledMessages.push(assembledMessage);
          continue;
        }
      }

      assembledMessages.push(assembledMessage);
    }

    // 按时间正序排序（最新消息在最后）
    return assembledMessages.sort((a, b) => {
      const timeA = (a.receiver_time ?? a.message_id_time) || 0;
      const timeB = (b.receiver_time ?? b.message_id_time) || 0;
      return timeA - timeB;
    });
  };

  /**
   * 创建私聊
   * @param params 创建私聊参数
   * @returns Promise<boolean> 是否创建成功
   */
  const createDirectChat = async (params: CreateDirectChatParams): Promise<boolean> => {
    setIsLoading(true);

    try {
      await ChatService.createDirectChat(params);
      return true;
    } catch (err: any) {
      console.error('创建私聊失败:', err);
      const errorMessage = err?.err_msg || '创建私聊失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 获取对话列表
   * @returns Promise<ConversationInfo[] | null> 对话列表结果
   */
  const getConversations = async (): Promise<ConversationInfo[] | null> => {
    setIsConversationsLoading(true);

    try {
      if (useFriendStore.getState().friendMap.size === 0) {
        await useFriendStore.getState().getFriendList();
      }
      if (useGroupStore.getState().groupMap.size === 0) {
        await useGroupStore.getState().getGroupList();
      }

      const rawConversations = await ChatService.getConversations();
      const assembledConversations = assembleConversationsData(rawConversations);
      chatStore.setConversations(assembledConversations);
      return assembledConversations;
    } catch (err: any) {
      console.error('获取对话列表失败:', err);
      const errorMessage = err?.err_msg || '获取对话列表失败';
      ToastUtils.showToastFail(errorMessage);
      return null;
    } finally {
      setIsConversationsLoading(false);
    }
  };

  /**
   * 获取对话列表，并且获取未读消息，然后将未读消息处理后设置到对话列表中
   */
  const getConversationsAndLastMessage = async (): Promise<ConversationInfo[] | null> => {
    let conversations = await getConversations();
    if (conversations) {
      // 使用批量并发处理来获取每个会话的未确认消息
      const results = await BatchProcessorUtils.processBatchWithFallback(
        conversations,
        async (conversation) => {
          const unackedMessages = await getUnackedMessages(conversation.id, {
            limit: 200
          });

          // 检查是否有 AT 自己的
          const atSelf = unackedMessages?.data?.some(message =>
            message.decryptedContentData?.messageType === MessageType.AT &&
            message.decryptedContentData?.attachment?.atUsers?.some(at => at.id === authData.userId));

          return {
            conversationId: conversation.id,
            unreadCount: unackedMessages?.data.length || 0,
            lastMessage: unackedMessages?.last_data ?? undefined,
            last_read_at: unackedMessages?.last_data?.receiver_time || null,
            atSelf: atSelf
          };
        },
        3 // 每次并发处理3个请求
      );

      // 将结果应用到对应的会话对象
      results.forEach((result, index) => {
        if (!(result instanceof Error)) {
          conversations![index].unreadCount = result.unreadCount;
          conversations![index].lastMessage = result.lastMessage;
          conversations![index].last_read_at = result.last_read_at;
          conversations![index].isAtSelf = result.atSelf;
        } else {
          // 如果请求失败，设置默认值
          logPrint(`获取会话 ${conversations![index].id} 的未读消息失败:`, result);
          conversations![index].unreadCount = 0;
          conversations![index].lastMessage = undefined;
          conversations![index].last_read_at = null;
          conversations![index].isAtSelf = false;
        }
      });
    }

    conversations = conversations ? sortConversations(conversations) : [];

    chatStore.setConversations(conversations);
    return conversations;
  }

  /**
   * 获取未确认消息
   * @param conversationId 对话ID
   * @param params 查询参数
   * @returns Promise<MessagesListResult | null> 未确认消息结果
   */
  const getUnackedMessages = async (
    conversationId: string,
    params?: UnackedMessagesParams
  ): Promise<MessagesListResult | null> => {

    try {
      const result = await ChatService.getUnackedMessages(conversationId, params);

      // 过滤掉自己的消息
      const filteredMessages = result.data.filter(message => message.sender_id !== authData.userId);

      // 解密消息内容
      const decryptedMessages = await MessageUtils.decryptMessages(filteredMessages);

      // 组装消息发送者信息
      const processedMessages = await assembleMessagesData(decryptedMessages);

      chatStore.setUnackedMessages(conversationId, processedMessages);

      const last_data = async () => {
        if (result.data?.length > 0) {
          const decryptedMessage = await MessageUtils.decryptMessages([result.data[0]]);
          const messages = await assembleMessagesData(decryptedMessage);
          return messages[0];
        }
        return null;
      }

      return {
        ...result,
        data: processedMessages,
        last_data: await last_data()
      };
    } catch (err: any) {
      console.error('获取未确认消息失败:', err);
      if (err.code === 800004) {
        console.error('获取未确认消息失败:', err);
        ToastUtils.showToastFail('会话不存在');
        getConversations();
        return null;
      } else {
        const errorMessage = err?.err_msg || '获取未确认消息失败';
        ToastUtils.showToastFail(errorMessage);
      }
      return null;
    } finally {
    }
  };

  /**
   * 获取消息
   * @param conversationId 对话ID
   * @param params 查询参数
   * @returns Promise<MessagesListResult | null> 消息结果
   */
  const getMessages = async (
    conversationId: string,
    params?: GetMessagesParams
  ): Promise<MessagesListResult | null> => {
    setIsMessagesLoading(true);

    try {
      const result = await ChatService.getMessages(conversationId, params);

      // 解密消息内容
      const decryptedMessages = await MessageUtils.decryptMessages(result.data);

      // 组装消息发送者信息
      const processedMessages = await assembleMessagesData(decryptedMessages);

      chatStore.setMessages(conversationId, processedMessages);
      logPrint(JSON.stringify(processedMessages));

      return {
        ...result,
        data: processedMessages
      };
    } catch (err: any) {
      console.error('获取消息失败:', err);
      if (err.code === 800004) {
        console.error('获取消息失败:', err);
        ToastUtils.showToastFail('会话不存在');
        getConversations();
        return null;
      } else {
        const errorMessage = err?.err_msg || '获取消息失败';
        ToastUtils.showToastFail(errorMessage);
      }
      return null;
    } finally {
      setIsMessagesLoading(false);
    }
  };

  /**
   * 加载更多历史消息
   * @param conversationId 对话ID
   * @param params 查询参数（包含 last_evaluated_key）
   * @returns Promise<MessagesListResult | null> 历史消息结果
   */
  const loadMoreMessages = async (
    conversationId: string,
    params?: GetMessagesParams
  ): Promise<MessagesListResult | null> => {
    try {
      const result = await ChatService.getMessages(conversationId, params);

      if (result && result.data.length > 0) {
        // 解密消息内容
        const decryptedMessages = await MessageUtils.decryptMessages(result.data);

        // 组装消息发送者信息
        const processedMessages = await assembleMessagesData(decryptedMessages);

        // 使用 prependMessages 方法将历史消息添加到开头
        chatStore.prependMessages(conversationId, processedMessages);

        logPrint('历史消息加载成功，数量:', processedMessages.length);

        return {
          ...result,
          data: processedMessages
        };
      } else {
        return null;
      }
    } catch (err: any) {
      console.error('加载历史消息失败:', err);
      const errorMessage = err?.err_msg || '加载历史消息失败';
      ToastUtils.showToastFail(errorMessage);
      return null;
    }
  };

  /**
   * 删除消息
   * @param conversationId 对话ID
   * @param messageId 消息ID
   * @returns Promise<boolean> 是否删除成功
   */
  const deleteMessage = async (conversationId: string, messageId: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      await ChatService.deleteMessage(conversationId, messageId);
      // 使用 store 的本地更新方法移除消息
      chatStore.removeMessage(conversationId, messageId);
      return true;
    } catch (err: any) {
      console.error('删除消息失败:', err);
      const errorMessage = err?.err_msg || '删除消息失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 删除会话历史记录
   * @param conversationId 对话ID
   * @returns Promise<boolean> 是否删除成功
   */
  const deleteConversationHistory = async (conversationId: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      await ChatService.deleteConversationHistory(conversationId);
      // 清空该对话的所有消息
      chatStore.setMessages(conversationId, []);
      chatStore.setUnackedMessages(conversationId, []);
      return true;
    } catch (err: any) {
      console.error('删除会话历史记录失败:', err);
      const errorMessage = err?.err_msg || '删除会话历史记录失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 已读部分消息
   */
  const ackMessages = async (conversationId: string, messageIds: string[], sessionType: SessionType): Promise<boolean> => {
    setIsLoading(true);
    try {
      const ackDatas = messageIds.map(messageId => ({
        conversation_id: conversationId,
        message_id: messageId,
        session_type: sessionType
      }));
      MessageManager.getInstance().ackMessages(ackDatas);
      return true;
    } catch (err: any) {
      console.error('已读部分消息失败:', err);
      const errorMessage = err?.err_msg || '已读部分消息失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 置顶对话
   * @param conversationId 对话ID
   * @returns Promise<boolean> 是否置顶成功
   */
  const pinConversation = async (conversationId: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      await ChatService.pinConversation(conversationId);

      // 更新本地对话状态
      const conversations = chatStore.conversations;
      const updatedConversations = conversations.map((conv: ConversationInfo) =>
        conv.id === conversationId ? { ...conv, is_pinned: true } : conv
      );

      // 重新排序对话列表
      const sortedConversations = sortConversations(updatedConversations);
      chatStore.setConversations(sortedConversations);

      ToastUtils.showToastSuccess('对话置顶成功');
      return true;
    } catch (err: any) {
      console.error('置顶对话失败:', err);
      const errorMessage = err?.err_msg || '置顶对话失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 取消置顶对话
   * @param conversationId 对话ID
   * @returns Promise<boolean> 是否取消置顶成功
   */
  const unpinConversation = async (conversationId: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      await ChatService.unpinConversation(conversationId);

      // 更新本地对话状态
      const conversations = chatStore.conversations;
      const updatedConversations = conversations.map((conv: ConversationInfo) =>
        conv.id === conversationId ? { ...conv, is_pinned: false } : conv
      );

      // 重新排序对话列表
      const sortedConversations = sortConversations(updatedConversations);
      chatStore.setConversations(sortedConversations);

      ToastUtils.showToastSuccess('取消置顶成功');
      return true;
    } catch (err: any) {
      console.error('取消置顶对话失败:', err);
      const errorMessage = err?.err_msg || '取消置顶对话失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 静音对话
   * @param conversationId 对话ID
   * @returns Promise<boolean> 是否静音成功
   */
  const muteConversation = async (conversationId: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      // TODO: 等待后端提供静音API接口
      // await ChatService.muteConversation(conversationId);

      // 临时实现：直接更新本地状态
      const conversations = chatStore.conversations;
      const updatedConversations = conversations.map((conv: ConversationInfo) =>
        conv.id === conversationId ? { ...conv, is_muted: true } : conv
      );
      chatStore.setConversations(updatedConversations);

      ToastUtils.showToastSuccess('对话静音成功');
      return true;
    } catch (err: any) {
      console.error('静音对话失败:', err);
      const errorMessage = err?.err_msg || '静音对话失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 取消静音对话
   * @param conversationId 对话ID
   * @returns Promise<boolean> 是否取消静音成功
   */
  const unmuteConversation = async (conversationId: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      // TODO: 等待后端提供取消静音API接口
      // await ChatService.unmuteConversation(conversationId);

      // 临时实现：直接更新本地状态
      const conversations = chatStore.conversations;
      const updatedConversations = conversations.map((conv: ConversationInfo) =>
        conv.id === conversationId ? { ...conv, is_muted: false } : conv
      );
      chatStore.setConversations(updatedConversations);

      ToastUtils.showToastSuccess('取消静音成功');
      return true;
    } catch (err: any) {
      console.error('取消静音对话失败:', err);
      const errorMessage = err?.err_msg || '取消静音对话失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 置顶群组消息
   * @param groupId 群组ID
   * @param messageId 消息ID
   * @returns Promise<boolean> 是否置顶成功
   */
  const pinGroupMessage = async (conversationId: string, groupId: string, messageId: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      ToastUtils.showLoading(true, '正在置顶消息...')

      // 调用API置顶消息
      await ChatService.pinGroupMessage(groupId, { message_id: messageId })

      // 发送置顶通知
      const messageManager = MessageManager.getInstance()
      await messageManager.sendGroupPinnedMessageNotification(
        conversationId,
        messageId,
        true // true 表示置顶
      )

      // 重新获取群组信息，刷新本地储存数据
      groupActions.getGroupInfo(groupId)

      ToastUtils.showToastSuccess('消息置顶成功');
      return true;
    } catch (err: any) {
      console.error('置顶群组消息失败:', err);
      const errorMessage = err?.err_msg || '置顶消息失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      ToastUtils.showLoading(false)
      setIsLoading(false);
    }
  };

  /**
   * 取消置顶群组消息
   * @param groupId 群组ID
   * @returns Promise<boolean> 是否取消置顶成功
   */
  const unpinGroupMessage = async (conversationId: string, groupId: string, messageId: string): Promise<boolean> => {
    setIsLoading(true);

    ToastUtils.showLoading(true, '正在取消置顶...')
    try {
      // 调用API取消置顶
      await ChatService.unpinGroupMessage(groupId)

      // 更新本地状态
      const { updateGroupInfo } = useGroupStore.getState()
      const groupInfo = useGroupStore.getState().groupInfoMap.get(groupId)

      if (groupInfo) {
        const updatedGroupInfo = {
          ...groupInfo,
          pinned_message: null
        }
        updateGroupInfo(groupId, updatedGroupInfo)
      }

      // 发送取消置顶通知
      await MessageManager.getInstance().sendGroupPinnedMessageNotification(
        conversationId,
        messageId,
        false // false 表示取消置顶
      )
      ToastUtils.showToastSuccess('取消置顶成功');
      return true;
    } catch (err: any) {
      console.error('取消置顶群组消息失败:', err);
      const errorMessage = err?.err_msg || '取消置顶失败，请重试';
      ToastUtils.showToastFail(errorMessage);
      return false;
    } finally {
      ToastUtils.showLoading(false)
      setIsLoading(false);
    }
  };


  return {
    // 状态
    isLoading,
    isConversationsLoading,
    isMessagesLoading,

    // 操作方法
    createDirectChat,
    getConversations,
    getConversationsAndLastMessage,
    getUnackedMessages,
    getMessages,
    loadMoreMessages,
    deleteMessage,
    deleteConversationHistory,
    assembleMessagesData,
    ackMessages,
    pinConversation,
    unpinConversation,
    pinGroupMessage,
    unpinGroupMessage,
    muteConversation,
    unmuteConversation,

    // 工具方法

  };
}
